import { useMutation } from '@apollo/client';
import { FormikProvider, useFormik } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { CREATE_USER } from '../../http/apollo-queries';
import { Button } from '../../components/UI';
import { InputCode } from '../../components/form';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '../../context/Auth';
import { useSignInContext } from './SignInContext';
import { Modal } from '../../components/Modal';
import { logger } from '../../helpers/logger';

const SignInConfirmEmail = () => {
  const { t, i18n } = useTranslation();
  const { confirmAccount, signIn, resendCode, refresh, refetch } = useAuth();
  const { state, dispatch } = useSignInContext();

  const navigate = useNavigate();
  const [createUserMutation] = useMutation(CREATE_USER);
  const [isLoading, setIsLoading] = useState(false);
  const [verifyEmailErr, setVerifyEmailErr] = useState('');
  const [isVerificationEmailResent, setIsVerificationEmailResent] =
    useState(false);

  const { email, password } = state;

  const formik = useFormik({
    initialValues: {
      code: '',
    },
    onSubmit: (values) => {
      setIsLoading(true);

      confirmAccount(email, values.code)
        .then((res) => {
          //after confirmation signIn with stored credentials
          return signIn(email, password);
        })
        .then((res) => {
          //create userMeta on db
          return createUserMutation({
            variables: {
              language: i18n.language.toUpperCase(),
            },
          });
        })
        .then((res) => {
          //needed to get a new token including cognito groups previous added
          return refresh();
        })
        .then((res) => {
          //refetch user to update user status
          return refetch();
        })
        .then((res) => {
          //clear stored credentials and proceed
          dispatch({
            type: 'CLEAR',
            payload: '',
          });
          navigate('/choose-username');
        })
        .catch((err) => {
          logger.error(err);
          setIsLoading(false);
          setVerifyEmailErr(
            t(`verifyEmail.form.error.${err.code}`, {
              defaultValue: t('verifyEmail.form.error.general'),
            })
          );
        });
    },
    validateOnMount: true,
  });

  const resendEmail = () => {
    resendCode(email!).then(() => {
      setIsVerificationEmailResent(true);
      setTimeout(() => {
        setIsVerificationEmailResent(false);
      }, 30000);
    });
  };

  return (
    <Modal
      isOpen={true}
      backdrop={'solid'}
      handleMerlinLogo={true}
      isClosable={false}
      padding={'huge'}
    >
      <div className={'text-center'}>
        <h1
          className={
            'text-black dark:text-white text-xl font-semibold text-center mb-4'
          }
        >
          {t('verifyEmail.title')}
        </h1>

        <p className={'text-grey-50 text-sm'}>
          <Trans i18nKey="verifyEmail.subtitle" email={email}>
            email sent to
            <span className="text-black font-semibold">{{ email }}</span>
            insert code
          </Trans>
        </p>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <div className="mt-12 mx-auto">
              <InputCode
                name="code"
                id="code"
                numberOfDigits={6}
                formik={formik}
                label={t('verifyEmail.form.label.verificationCode')}
                errorMessage={verifyEmailErr}
              />
            </div>
            <div className="mt-10">
              <Button
                type="submit"
                variant="primary"
                buttonClass="w-64"
                loading={isLoading}
              >
                {t('verifyEmail.continue')}
              </Button>
            </div>
          </form>
        </FormikProvider>
        <div className="mt-5">
          <span className="text-grey-80">
            {t(
              !isVerificationEmailResent
                ? 'verifyEmail.notReceived'
                : 'verifyEmail.resent'
            )}
          </span>
          <button
            className="text-sm text-black font-semibold"
            onClick={() => resendEmail()}
          >
            {!isVerificationEmailResent && t('verifyEmail.sendNew')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SignInConfirmEmail;
